/**
 * El esquema de instalación que más usa ONFASE, dentro de una vivienda en
 * corte (sin fachada ni pared izquierda, y con la losa recortada al frente):
 * 4 paneles de 630 W sobre la losa → 10 m de cable DC con MC4 que baja por la
 * losa → caja de protecciones → inversor híbrido ↔ batería MUST; salida AC →
 * refrigerador, TV y lámpara; red eléctrica por el metro contador; puesta a
 * tierra con varilla de cobre y 7 m de cable.
 *
 * Unidades: 1 ≈ 10 cm. x a la derecha, y hacia el fondo, z hacia arriba.
 */
import {
  caja, calcoDerecha, calcoFrente, calcoPerfil, discoFrente, extruye, losetas,
  type Figura, type Linea, type Malla, type V3,
} from './motor';

/** Momento del día y si hay corriente en la calle: se combinan libremente. */
export interface Estado {
  noche: boolean;
  apagon: boolean;
}
/** 1 = energía en el sentido del cable, -1 = al revés, 0 = quieto, 'corte' = sin corriente */
export type Flujo = 1 | -1 | 0 | 'corte';

export type IdComponente =
  | 'paneles' | 'cables' | 'caja' | 'inversor' | 'bateria' | 'tierra' | 'metro' | 'casa' | 'coche';

export interface Componente {
  id: IdComponente;
  titulo: string;
  texto: string;
  ancla: V3;
}

export const COMPONENTES: Componente[] = [
  {
    id: 'paneles', ancla: [-1, 9.5, 33.5],
    titulo: '4 paneles solares de 630 W',
    texto: '2,52 kW de potencia pico sobre la losa de la casa. Convierten la luz del sol en corriente continua (DC). Van en estructura inclinada para aprovechar al máximo el sol de Cuba.',
  },
  {
    id: 'cables', ancla: [-25.4, 13.6, 20],
    titulo: 'Cable solar DC · 10 m',
    texto: 'Cable rojo (+) y negro (−) con conectores MC4: salen de los paneles por la losa, bajan por fuera de la casa y entran hasta la caja de protecciones, junto al inversor.',
  },
  {
    id: 'caja', ancla: [-5, 12.6, 19.2],
    titulo: 'Caja de protecciones',
    texto: 'Breakers DC y AC, protector contra sobretensiones y medidor de voltaje y corriente. Protege cada parte del sistema y te deja ver lo que está pasando.',
  },
  {
    id: 'inversor', ancla: [-17, 12.4, 20.2],
    titulo: 'Inversor híbrido',
    texto: 'El cerebro del sistema: convierte la energía de los paneles y de la batería en 110/220 V para tu casa, carga la batería y decide de dónde tomar la corriente.',
  },
  {
    id: 'bateria', ancla: [-17.5, 7, 11.8],
    titulo: 'Batería de Litio',
    texto: 'Guarda la energía que sobra durante el día para usarla de noche y en los apagones. Tecnología LiFePO4: segura y de larga vida.',
  },
  {
    id: 'tierra', ancla: [-4, -10, 3.4],
    titulo: 'Puesta a tierra · 7 m',
    texto: 'Varilla de cobre enterrada en el patio y cable amarillo-verde: descarga a tierra cualquier falla o sobretensión para proteger tus equipos y a tu familia.',
  },
  {
    id: 'metro', ancla: [26.6, -5, 14.8],
    titulo: 'Metro contador · red eléctrica',
    texto: 'La corriente de la calle entra por el metro contador. Si hay corriente, el sistema la puede usar de respaldo; si hay apagón, tu casa sigue con la batería y los paneles.',
  },
  {
    id: 'casa', ancla: [16.7, 6, 17],
    titulo: 'Tus equipos',
    texto: 'Refrigerador, TV, luces y ventiladores reciben 110/220 V estables del inversor y siguen funcionando aunque se vaya la corriente.',
  },
  {
    id: 'coche', ancla: [29.5, -11, 8.4],
    titulo: 'Carga de coche eléctrico',
    texto: 'Con un cargador de pared conectado al sistema, cargas tu auto o moto eléctrica con la energía del sol: sin gasolina y sin depender de la corriente de la calle.',
  },
];

/** Título y explicación de cada combinación de momento del día y red. */
export function explicacion({ noche, apagon }: Estado): { titulo: string; texto: string } {
  if (!noche && !apagon) {
    return {
      titulo: 'Día · con corriente',
      texto: 'El sol genera energía: los paneles alimentan la casa, cargan la batería y también el coche eléctrico. La red queda de respaldo.',
    };
  }
  if (!noche && apagon) {
    return {
      titulo: 'Día · apagón',
      texto: 'Se fue la corriente, pero de día ni lo notas: los paneles siguen alimentando la casa y cargando la batería y el coche.',
    };
  }
  if (noche && !apagon) {
    return {
      titulo: 'Noche · con corriente',
      texto: 'Sin sol, la batería alimenta la casa con la energía que guardó durante el día. La red queda de respaldo.',
    };
  }
  return {
    titulo: 'Noche · apagón',
    texto: 'Se fue la corriente y ya no hay sol, pero no en tu casa: el inversor sigue con la batería por su cuenta, sin que tengas que hacer nada.',
  };
}

/** Qué cables llevan energía, y en qué sentido, en cada momento. */
export function flujos({ noche, apagon }: Estado): Record<string, Flujo> {
  const dia = !noche;
  const corte: Flujo = apagon ? 'corte' : 0;
  return {
    pv: dia ? 1 : 0,
    dc: dia ? 1 : 0,
    bat: dia ? 1 : -1,
    acInv: 1,
    acCasa: 1,
    ev: dia ? 1 : 0,
    red: corte,
    acMetro: corte,
    tierra: 0,
  };
}

// ─── Geometría ───────────────────────────────────────────────────────────────

const ROJO = '#dc2626', NEGRO = '#111827', AZUL = '#1d4ed8', MARRON = '#b45309';

// La casa: x -24…20, y -4…15.2, paredes de 26 (2,6 m); la losa cubre de y = 3 al fondo.
const PISO = 0.8, ALTO = 26, LOSA = 1.6, LOSA_Y = 3;
const TECHO = ALTO + LOSA;
/** Altura media de los paneles (hacia donde apuntan los rayos del sol). */
export const TECHO_PANELES = TECHO + 3;

const CONTORNO = '#1f2a44';

/** Contorno de ilustración a lo largo de una arista. */
const arista = (a: V3, b: V3, capa = 1): Linea => ({ pts: [a, b], color: CONTORNO, ancho: 1.1, capa });

export function escena(noche: boolean): { mallas: Malla[]; lineas: Linea[]; figuras: Figura[] } {
  const mallas: Malla[] = [];
  const lineas: Linea[] = [];
  const figuras: Figura[] = [];

  // Terreno y piso de la casa
  mallas.push(caja(-30, -8, -1.2, 70, 27, 1.2, { arriba: noche ? '#7fa36a' : '#a7d48a', lados: '#8b7355' }, 0));
  mallas.push(caja(-30, -15, -1.2, 70, 7, 1.2, { arriba: noche ? '#9ca3af' : '#d9dde3', lados: '#9ca3af' }, 0));
  mallas.push({
    capa: 0.5,
    caras: [
      ...losetas('z', PISO, -24, 21.2, -4, 15.2, 6, '#ece6dc', [0, 0, 1]),
      ...losetas('y', -4, -24, 21.2, 0, PISO, 6, '#cfc6b6', [0, -1, 0]),
      ...losetas('x', -24, -4, 15.2, 0, PISO, 6, '#cfc6b6', [-1, 0, 0]),
    ],
  });
  // Alfombra
  mallas.push(caja(1, -1, PISO, 10, 7, 0.05, '#bfdbfe'));

  // Pared del fondo (cara interior) y pared derecha (cara interior)
  // Paredes: siempre detrás de lo que hay dentro de la casa (capa 0.6)
  mallas.push({
    capa: 0.6,
    caras: [
      ...losetas('y', 14, -24, 21.2, PISO, ALTO, 3, '#f3eee6', [0, -1, 0]),
      ...losetas('x', 20, -4, 14, PISO, ALTO, 3, '#ebe4d8', [-1, 0, 0]),
      // Cantos de la pared derecha y de la del fondo (corte)
      ...losetas('y', -4, 20, 21.2, 0, ALTO, 3, '#e2dace', [0, -1, 0]),
      ...losetas('z', ALTO, 20, 21.2, -4, LOSA_Y, 3, '#c9bfae', [0, 0, 1]),
      ...losetas('x', -24, 14, 15.2, 0, ALTO, 3, '#c9bfae', [-1, 0, 0]),
    ],
  });
  // Columnas de las esquinas
  // El muro bajo va en capa 2.5: siempre delante de la losa
  mallas.push(caja(-24.6, 13.4, 0, 1.2, 1.2, ALTO, '#d6d0c6'));
  // Muro bajo de la fachada, cortado
  mallas.push(caja(-23.4, -4.6, 0, 43.4, 0.6, 3.2, { lados: '#c9bfae', arriba: '#b8ad9a' }, 2.5));

  // Losa plana (recortada al frente para ver adentro). Capa 2: tapa lo que
  // queda debajo; los paneles y los cables de encima van en capa 3.
  mallas.push({
    capa: 2,
    caras: [
      ...losetas('z', TECHO, -24.8, 21.2, LOSA_Y, 15.2, 3, '#d4cfc6', [0, 0, 1]),
      ...losetas('y', LOSA_Y, -24.8, 21.2, ALTO, TECHO, 3, '#a8a29a', [0, -1, 0]),
      ...losetas('x', -24.8, LOSA_Y, 15.2, ALTO, TECHO, 3, '#b5afa5', [-1, 0, 0]),
    ],
  });

  // Contornos de la losa y de la estructura
  lineas.push(
    arista([-24.8, LOSA_Y, TECHO], [21.2, LOSA_Y, TECHO], 2),
    arista([-24.8, LOSA_Y, ALTO], [21.2, LOSA_Y, ALTO], 2),
    arista([-24.8, LOSA_Y, TECHO], [-24.8, 15.2, TECHO], 2),
    arista([-24.8, LOSA_Y, ALTO], [-24.8, 15.2, ALTO], 2),
    arista([-24.8, LOSA_Y, ALTO], [-24.8, LOSA_Y, TECHO], 2),
    arista([21.2, LOSA_Y, ALTO], [21.2, LOSA_Y, TECHO], 2),
    arista([-24, 14, PISO], [-24, 14, ALTO], 0.6),
    arista([20, -4, 0], [20, -4, ALTO], 0.6),
    arista([21.2, -4, 0], [21.2, -4, ALTO], 0.6),
    arista([20, -4, ALTO], [21.2, -4, ALTO], 0.6),
    arista([20, -4, ALTO], [20, LOSA_Y, ALTO], 0.6),
    arista([-24, -4, PISO], [20, -4, PISO], 0.55),
    arista([-24, -4, PISO], [-24, 15.2, PISO], 0.55),
    arista([-24, -4, 0], [-24, -4, PISO], 0.55),
  );

  // ─── Equipos ─────────────────────────────────────────────────────────────
  // Batería MUST sobre ruedas
  for (const [x, y] of [[-20.6, 7.3], [-15.4, 7.3], [-20.6, 10.3], [-15.4, 10.3]]) {
    mallas.push(caja(x, y, PISO, 1, 1, 0.8, '#1f2937'));
  }
  const zb = PISO + 0.8;
  const bat = caja(-21, 7, zb, 7, 4.5, 9, { lados: '#e2e8f0', frente: '#f8fafc', arriba: '#f1f5f9' });
  bat.caras.push(
    discoFrente(-17.5, 7, zb + 6.3, 1.3, '#0f172a', true),
    discoFrente(-17.5, 7, zb + 6.3, 0.8, noche ? '#10b981' : '#34d399', true),
    calcoFrente(-18.8, 7, zb + 1.8, 2.6, 0.7, '#dc2626', true),
  );
  mallas.push(bat);

  // Inversor híbrido en la pared del fondo
  const inv = caja(-21, 12.4, 11.6, 8, 1.6, 7.6, { lados: '#e2e8f0', frente: '#f8fafc', arriba: '#f1f5f9' });
  inv.caras.push(
    discoFrente(-17, 12.4, 16, 1.6, '#111827', true),
    calcoFrente(-17.8, 12.4, 15.5, 1.6, 1, '#38bdf8', true),
    calcoDerecha(-13, 12.4, 12, 1.6, 7, '#334155'),
  );
  mallas.push(inv);

  // Caja de protecciones con sus breakers y medidor
  const cp = caja(-10, 12.6, 11.6, 10, 1.4, 6.8, { lados: '#e5e7eb', frente: '#f9fafb' });
  cp.caras.push(
    calcoFrente(-9.3, 12.6, 13.4, 1.2, 3, '#2563eb', true),
    calcoFrente(-7.8, 12.6, 13.4, 1.5, 3, '#111827', true),
    calcoFrente(-7.5, 12.6, 15.2, 0.9, 0.5, '#ef4444', true),
    calcoFrente(-7.5, 12.6, 14.1, 0.9, 0.5, '#22c55e', true),
    calcoFrente(-6, 12.6, 13.4, 1.8, 3, '#d1d5db', true),
    calcoFrente(-3.9, 12.6, 13.4, 1.1, 3, '#1d4ed8', true),
    calcoFrente(-2.5, 12.6, 13.4, 1.2, 3, '#dc2626', true),
    calcoFrente(-1.1, 12.6, 13.4, 0.9, 3, '#ef4444', true),
  );
  mallas.push(cp);

  // ─── Equipos de la casa ──────────────────────────────────────────────────
  // Refrigerador
  const nevera = caja(14.5, 6, PISO, 4.6, 4, 15, { lados: '#e5e7eb', frente: '#f8fafc', arriba: '#f1f5f9' });
  nevera.caras.push(
    calcoFrente(14.5, 6, PISO + 9.6, 4.6, 0.12, '#cbd5e1'),
    calcoFrente(15, 6, PISO + 10.4, 0.35, 3, '#94a3b8'),
    calcoFrente(15, 6, PISO + 5.5, 0.35, 3, '#94a3b8'),
  );
  mallas.push(nevera);

  // TV en la pared del fondo, encendido
  const tv = caja(3, 13.3, 8.5, 8.4, 0.6, 4.8, '#111827');
  tv.caras.push(calcoFrente(3.3, 13.3, 8.8, 7.8, 4.2, noche ? '#38bdf8' : '#0ea5e9', true));
  mallas.push(tv);
  mallas.push(caja(4.5, 11, PISO, 5.4, 2.6, 5.8, '#a16207'));

  // Lámpara de pie (se enciende de noche)
  mallas.push(caja(-2.2, 1.8, PISO, 0.3, 0.3, 11, '#9ca3af'));
  mallas.push(caja(-3.3, 0.8, 11.8, 2.5, 2.5, 1.8, noche ? '#fde047' : '#e5e7eb'));

  // Mesa de centro
  mallas.push(caja(4, 5.6, PISO + 2.2, 5, 3, 0.5, '#d9a066'));
  for (const [x, y] of [[4.3, 5.9], [8.4, 5.9], [4.3, 8], [8.4, 8]]) mallas.push(caja(x, y, PISO, 0.35, 0.35, 2.2, '#b07a45'));

  // Ventana en la pared derecha (capa 0.7: sobre la pared, detrás de los muebles)
  mallas.push({
    capa: 0.7,
    caras: [
      { pts: [[19.97, -1.5, 9], [19.97, 4.5, 9], [19.97, 4.5, 17], [19.97, -1.5, 17]], n: [-1, 0, 0], color: '#e5e7eb' },
      { pts: [[19.94, -1, 9.5], [19.94, 4, 9.5], [19.94, 4, 16.5], [19.94, -1, 16.5]], n: [-1, 0, 0], color: noche ? '#1e3a5f' : '#93c5fd', plano: true },
    ],
  });

  // Plantas y arbustos
  figuras.push(
    { pos: [-1, -2.2, PISO], tipo: 'maceta' },
    { pos: [-21.5, -2, PISO], tipo: 'maceta', escala: 0.9 },
    { pos: [32.5, 15, 0], tipo: 'arbusto' },
    { pos: [23, 15.5, 0], tipo: 'arbusto', escala: 0.8 },
    { pos: [-27.5, 8, 0], tipo: 'arbusto', escala: 0.9 },
    { pos: [-27, -6.5, 0], tipo: 'maceta', escala: 1.1, capa: 2.5 },
  );

  // Sofá mirando al TV
  mallas.push(caja(2, 1.2, PISO, 10, 3.6, 2.6, '#64748b'));
  mallas.push(caja(2, 0.2, PISO, 10, 1.1, 5.4, '#475569'));

  // ─── Coche eléctrico moderno (hatchback) en la entrada, con su cargador ────
  // Silueta de perfil extruida: parte trasera, techo, parabrisas, capó y frente.
  const perfil: [number, number][] = [
    [23.4, 0.9], [23, 2.2], [23.2, 4], [24, 6], [29.6, 6.3], [32.4, 4.3], [35.6, 3.5], [36.1, 2], [35.6, 0.9],
  ];
  const VIDRIO = '#1e3a4a';
  const coche = extruye(perfil, -14, -8, '#14b8c4', [
    '#0f2a33', '#12a7b3', VIDRIO, '#0f172a', VIDRIO, '#2dd4d4', '#12a7b3', '#0f2a33', '#0f172a',
  ]);
  coche.caras.push(
    // Contorno negro de las ventanas y los vidrios laterales
    calcoPerfil([[24.3, 4.2], [24.9, 5.75], [29.4, 6.02], [31.9, 4.25]], -14, '#0f172a'),
    calcoPerfil([[24.8, 4.45], [25.2, 5.5], [27.4, 5.64], [27.4, 4.42]], -14, '#5b8a99', true),
    calcoPerfil([[27.8, 4.42], [27.8, 5.66], [29.3, 5.74], [31.2, 4.42]], -14, '#7fb0bd', true),
    // Faros LED, luz trasera y moldura inferior
    calcoPerfil([[34.3, 3.05], [35.8, 3.28], [35.7, 3.52], [34.4, 3.34]], -14, '#e0f2fe', true),
    calcoPerfil([[23.15, 3.45], [24, 3.45], [24, 3.85], [23.2, 3.85]], -14, '#ef4444', true),
    calcoPerfil([[24.2, 1], [35, 1], [35, 1.35], [24.2, 1.35]], -14, '#0e7490'),
    // Ruedas con llantas plateadas
    discoFrente(25.9, -14, 1.5, 1.75, '#0f172a', true),
    discoFrente(25.9, -14, 1.5, 1.15, '#cbd5e1', true),
    discoFrente(25.9, -14, 1.5, 0.5, '#64748b', true),
    discoFrente(33.4, -14, 1.5, 1.75, '#0f172a', true),
    discoFrente(33.4, -14, 1.5, 1.15, '#cbd5e1', true),
    discoFrente(33.4, -14, 1.5, 0.5, '#64748b', true),
  );
  mallas.push(coche);
  // Espejo y línea de la puerta
  mallas.push(caja(31.6, -14.6, 4.1, 0.8, 0.6, 0.45, '#0f172a'));
  lineas.push({ pts: [[28.9, -14.06, 1.4], [28.9, -14.06, 4.3]], color: '#0e7490', ancho: 0.9 });
  mallas.push(caja(22.2, -6.6, 0, 1, 1, 8, '#cbd5e1'));
  const cargador = caja(21.9, -7.4, 5, 1.6, 0.9, 3.6, { lados: '#e5e7eb', frente: '#f8fafc' });
  cargador.caras.push(calcoFrente(22.4, -7.4, 7.2, 0.6, 0.6, '#22c55e', true));
  mallas.push(cargador);

  // ─── Paneles sobre la losa: 4 × 630 W ────────────────────────────────────
  const zf = TECHO + 0.9, zt = TECHO + 5.2;
  for (let i = 0; i < 4; i++) {
    const x0 = -12.2 + i * 5.1, x1 = x0 + 4.7;
    mallas.push(caja(x0 + 0.3, 5.6, TECHO, 0.3, 0.3, 0.9, '#94a3b8', 3));
    mallas.push(caja(x1 - 0.6, 5.6, TECHO, 0.3, 0.3, 0.9, '#94a3b8', 3));
    mallas.push(caja(x0 + 0.3, 12.6, TECHO, 0.3, 0.3, 5.2, '#94a3b8', 3));
    mallas.push(caja(x1 - 0.6, 12.6, TECHO, 0.3, 0.3, 5.2, '#94a3b8', 3));
    const t: V3[] = [[x0, 5.3, zf], [x1, 5.3, zf], [x1, 13, zt], [x0, 13, zt]];
    const b: V3[] = t.map(([x, y, z]) => [x, y, z - 0.35]);
    const marco = '#cbd5e1';
    mallas.push({
      capa: 3,
      caras: [
        { pts: t, color: '#1e3a8a' },
        { pts: [b[0], b[1], b[2], b[3]], color: marco },
        { pts: [t[0], t[1], b[1], b[0]], color: marco },
        { pts: [t[3], t[2], b[2], b[3]], color: marco },
        { pts: [t[0], t[3], b[3], b[0]], color: marco },
        { pts: [t[1], t[2], b[2], b[1]], color: marco },
      ],
    });
    for (const f of [1 / 3, 2 / 3]) {
      const x = x0 + (x1 - x0) * f;
      lineas.push({ pts: [[x, 5.3, zf + 0.03], [x, 13, zt + 0.03]], color: '#3b82f6', ancho: 0.7, capa: 3 });
    }
    for (const f of [0.2, 0.4, 0.6, 0.8]) {
      const y = 5.3 + 7.7 * f, z = zf + 0.03 + (zt - zf) * f;
      lineas.push({ pts: [[x0, y, z], [x1, y, z]], color: '#3b82f6', ancho: 0.7, capa: 3 });
    }
  }

  // ─── Afuera: metro contador, poste y varilla de tierra ───────────────────
  // Metro contador al frente de la casa: ninguna pared lo tapa al girar
  mallas.push(caja(25.5, -4.2, 0, 2.2, 2.2, 9, '#cbd5e1'));
  const metro = caja(25.3, -5, 9, 2.6, 1.4, 4.4, { lados: '#e5e7eb', frente: '#f1f5f9' });
  metro.caras.push(calcoFrente(25.7, -5, 11.6, 1.8, 0.9, '#1e293b', true));
  mallas.push(metro);
  mallas.push(caja(33, 6, 0, 0.8, 0.8, 32, '#94a3b8'));
  mallas.push(caja(31.7, 6, 30, 3.4, 0.8, 0.5, '#94a3b8'));
  mallas.push(caja(-4.2, -10.2, 0, 0.4, 0.4, 2.2, '#b87333'));
  mallas.push(caja(-4.5, -10.5, 1.5, 1, 1, 0.5, '#a16207'));

  // ─── Cables ──────────────────────────────────────────────────────────────
  const zl = TECHO + 0.25; // cables sobre la losa
  // DC de los paneles a la caja: por encima de la losa y bajando a través de ella
  lineas.push(
    // Recorrido visible: por la losa hasta el borde izquierdo, baja por fuera junto
    // a la columna de la esquina y entra por la pared del fondo hasta la caja.
    { id: 'pv', color: ROJO, ancho: 2.2, capaAlta: [ALTO - 0.1, 3], flecha: true, pts: [
      [-11.4, 5.6, zf - 0.4], [-11.4, 5.6, zl], [-11.4, 13.9, zl], [-24.4, 13.9, zl], [-25.4, 13.9, TECHO - 0.3],
      [-25.4, 13.9, 10.9], [-23.8, 13.7, 10.9], [-9.6, 13.7, 10.9], [-9.6, 13.7, 11.6],
    ] },
    { id: 'pv', color: NEGRO, ancho: 2.2, capaAlta: [ALTO - 0.1, 3], pts: [
      [-10.8, 5.6, zf - 0.4], [-10.8, 5.6, zl], [-10.8, 13.3, zl], [-24.4, 13.3, zl], [-25.4, 13.3, TECHO - 0.3],
      [-25.4, 13.3, 10.3], [-23.8, 13.2, 10.3], [-8.9, 13.2, 10.3], [-8.9, 13.2, 11.6],
    ] },
  );
  // DC de la caja al inversor, y AC del inversor a la caja
  lineas.push(
    { id: 'dc', color: ROJO, ancho: 1.8, flecha: true, pts: [[-10, 13.3, 12.6], [-13, 13.3, 12.6]] },
    { id: 'dc', color: NEGRO, ancho: 1.8, pts: [[-10, 13.3, 13.3], [-13, 13.3, 13.3]] },
    { id: 'acInv', color: MARRON, ancho: 1.8, flecha: true, pts: [[-13, 13.3, 14.2], [-10, 13.3, 14.2]] },
    { id: 'acInv', color: AZUL, ancho: 1.8, pts: [[-13, 13.3, 14.9], [-10, 13.3, 14.9]] },
  );
  // Batería ↔ inversor
  lineas.push(
    { id: 'bat', color: ROJO, ancho: 2.4, flecha: true, pts: [[-19.3, 13.2, 11.6], [-19.3, 13.2, 11.1], [-19.3, 9.5, 11.1], [-19.3, 9.5, zb + 9]] },
    { id: 'bat', color: NEGRO, ancho: 2.4, pts: [[-15.7, 13.2, 11.6], [-15.7, 13.2, 11.1], [-15.7, 9.5, 11.1], [-15.7, 9.5, zb + 9]] },
  );
  // AC de la caja a los equipos: TV y refrigerador
  lineas.push(
    { id: 'acCasa', color: ROJO, ancho: 1.8, flecha: true, pts: [[0, 13.5, 15], [1.8, 13.5, 15], [1.8, 13.5, 11], [3, 13.5, 11]] },
    { id: 'acCasa', color: ROJO, ancho: 1.8, flecha: true, pts: [[-1.8, 13.4, 11.6], [-1.8, 13.4, PISO + 0.15], [16.2, 13.4, PISO + 0.15], [16.2, 10, PISO + 0.15], [16.2, 10, 3]] },
    { id: 'acCasa', color: AZUL, ancho: 1.8, pts: [[-1.2, 12.9, 11.6], [-1.2, 12.9, PISO + 0.15], [16.9, 12.9, PISO + 0.15], [16.9, 10, PISO + 0.15], [16.9, 10, 3]] },
  );
  // Del sistema al cargador del coche, y del cargador al coche
  lineas.push(
    { id: 'ev', color: NEGRO, ancho: 1.8, pts: [[-0.4, 13.8, 11.6], [-0.4, 13.8, PISO + 0.15], [19.6, 13.8, PISO + 0.15], [19.6, -3.2, PISO + 0.15], [21.8, -3.2, 0.2], [22.7, -6.5, 0.2], [22.7, -6.5, 5]] },
    { id: 'ev', color: NEGRO, ancho: 1.8, flecha: true, pts: [[22.7, -7.5, 5.4], [22.9, -9, 3.4], [23.1, -10.6, 3.2]] },
  );
  // Red eléctrica: poste → metro contador → caja
  lineas.push(
    { id: 'red', color: '#475569', ancho: 1.5, pts: [[33.4, 6.4, 30], [30, 1, 22], [26.6, -4.6, 13.4]] },
    { id: 'acMetro', color: ROJO, ancho: 1.7, flecha: true, pts: [[26.2, -5.1, 9], [26.2, -5.1, 0.2], [22, -5.1, 0.2], [21.4, -3.6, 0.2], [19.4, -3.6, PISO + 0.15], [19.4, 12.4, PISO + 0.15], [-0.6, 12.4, PISO + 0.15], [-0.6, 12.4, 11.6]] },
    { id: 'acMetro', color: AZUL, ancho: 1.7, pts: [[26.9, -5.1, 9], [26.9, -5.1, 0.4], [22.4, -5.6, 0.4], [21.6, -3.1, 0.4], [18.8, -3.1, PISO + 0.15], [18.8, 11.8, PISO + 0.15], [-0.1, 11.8, PISO + 0.15], [-0.1, 11.8, 11.6]] },
  );
  // Tierra (7 m, amarillo-verde) hasta la varilla del patio
  lineas.push({
    id: 'tierra', color: '#65a30d', ancho: 1.8,
    pts: [[-8.5, 12.5, 11.6], [-8.5, 12.5, PISO + 0.15], [-8.5, -3.8, PISO + 0.15], [-8.5, -5, 0.2], [-8.5, -10, 0.2], [-4.4, -10, 0.2], [-4, -10, 1.8]],
  });

  return { mallas, lineas, figuras };
}
